import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import dayjs from "dayjs";
import { useDispatch } from "../redux/store";
import { getBookingTimesV2 } from "../utils/api";
import {
  getShopNow,
  resolveSameDayBookings,
} from "../utils/sameDayBooking";
import { AppointmentState } from "../types";
import { updateAppointment } from "../redux/slices/appointmentSlice";
import { AppDispatch } from "../redux/store";
import {
  StayOrDropSelector,
  DatePicker,
  TimePicker,
  RideOptionPicker,
  AppointmentSummary,
  AfterHoursDropOffCard,
} from "../components";
import { ProspectiveCalendarAppointment } from "../types/calendar-appointment.interface";
import { AvailableSlotsInterfaceV2 } from "../types/available-slots.interface";
import { ApptType } from "../types/booking-config.interface";
import { getGradientFromColor } from "../utils/theme";
import { scrollIntoViewOnMobile, scrollIntoViewSmooth } from "../utils/dom";
import { compileTemplate, getScheduleTimes } from "../utils/formatting";

interface AppointmentPickerProps {
  appointment: AppointmentState;
}

const TRANSPORT_TYPES = [
  { flag: "shuttleEnabled", label: "Shuttle" },
  { flag: "loanerEnabled", label: "Loaner" },
  { flag: "rentalEnabled", label: "Rental Car" },
] as const;

const AppointmentPickerV2: React.FC<AppointmentPickerProps> = ({
  appointment,
}) => {
  const dispatch = useDispatch<AppDispatch>();

  const [stayOrDropOff, setStayOrDropOff] = useState<ApptType | undefined>(
    (appointment?.apptType as ApptType | undefined) ||
      appointment?.slot?.dropOff
  );

  const sameDayBookings = resolveSameDayBookings(
    appointment?.shopData,
    stayOrDropOff
  );
  const [selectedDate, setSelectedDate] = useState<string | null>(
    appointment?.date
      ? dayjs(appointment.date, "MM/DD/YYYY").format("YYYY-MM-DD")
      : appointment?.slot?.date
        ? appointment.slot.date
        : null
  );
  const [selectedTime, setSelectedTime] = useState<string>(
    appointment?.time || appointment?.slot?.time || ""
  );
  const [complimentaryRide, setComplimentaryRide] = useState<string>(
    appointment?.complimentaryRide || ""
  );

  const [noSlotsMessage, setNoSlotsMessage] = useState<string>(
    "Unfortunately, there are no available time slots at the moment. Please try again later or contact us directly."
  );

  const generateServiceSpecificMessage = useCallback((currentAppointment: AppointmentState): string => {
    const selectedServices = currentAppointment?.services?.map((s) => s.name) || [];
    const selectedService =
      currentAppointment?.service || selectedServices[0] || "your selected service";

    if (!currentAppointment?.service && selectedServices.length === 0) {
      return "Unfortunately, there are no available time slots at the moment. Please try again later or contact us directly.";
    }

    const serviceText =
      selectedServices.length > 1
        ? `${selectedServices.join(", ")} services`
        : selectedService;

    return `Unfortunately, there are no available time slots for ${serviceText} on this day. This may be due to service-specific scheduling restrictions or high demand. Please try selecting a different date or contact us directly to discuss alternative options.`;
  }, []);

  const appointmentRef = useRef(appointment);
  const previousStayOrDropOffRef = useRef(stayOrDropOff);
  const previousSelectedDateRef = useRef(selectedDate);
  const previousSelectedTimeRef = useRef(selectedTime);
  const previousComplimentaryRideRef = useRef(complimentaryRide);
  const previousAfterHoursRequestedRef = useRef(
    !!appointment?.afterHoursDropOffRequested
  );
  const previousAppointmentOptionsStepRef = useRef(
    appointment?.appointmentOptionsStep
  );

  useEffect(() => {
    appointmentRef.current = appointment;
  }, [appointment]);

  // Month-based calendar state (replaces old 7-day paging)
  const [currentMonth, setCurrentMonth] = useState<dayjs.Dayjs>(
    appointment?.date
      ? dayjs(appointment.date, "MM/DD/YYYY").startOf("month")
      : getShopNow(appointment?.shopData).startOf("month")
  );

  // Ref to skip clearing logic on initial mount (preserves restored state)
  const isInitialMount = useRef(true);
  const [bounceComplimentaryRide, setBounceComplimentaryRide] =
    useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [, setClear] = useState<boolean>(false);

  // Check if any transportation option is enabled
  const hasAnyTransportOption = useMemo(() => {
    const shop = appointment?.shopData || {};
    return TRANSPORT_TYPES.some((t) => shop[t.flag as keyof typeof shop]);
  }, [appointment?.shopData]);

  const afterHoursRequested = !!appointment?.afterHoursDropOffRequested;

  // Derive whether the appointment is fully selected
  const isAppointmentComplete = Boolean(
    stayOrDropOff &&
    selectedDate &&
    selectedTime &&
    (complimentaryRide || !hasAnyTransportOption || stayOrDropOff !== "drop")
  );

  const isCommentsStep = appointment?.appointmentOptionsStep === "comments";

  ///////////
  const [availabilityResponse, setAvailabilityResponse] = useState<
    AvailableSlotsInterfaceV2 | undefined
  >(undefined);

  //build handlebars
  useEffect(() => {
    const result = compileTemplate(
      appointment?.shopData?.messages?.noSlots ??
        "Unfortunately, there are no available time slots at the moment. Please try again later or contact us directly.",
      {
        your_service: appointment?.service,
        on_date: appointment?.date,
        at_time: appointment?.time,
      }
    );
    setNoSlotsMessage(result);
  }, [
    appointment?.date,
    appointment?.service,
    appointment?.shopData?.messages?.noSlots,
    appointment?.time,
  ]);

  // load values from redux
  useEffect(() => {
    const hasApptType = Boolean(stayOrDropOff);
    const hasDate = Boolean(selectedDate);
    const hasTime = Boolean(selectedTime);
    const requiresRide = hasAnyTransportOption && stayOrDropOff === "drop";
    const hasRide = !requiresRide || Boolean(complimentaryRide);
    const requiresComments = Boolean(appointment?.shopData?.requireComments);
    const hasComments =
      !requiresComments || Boolean(appointment?.comments?.trim());

    setClear(hasApptType && hasDate && hasTime && hasRide && hasComments);
  }, [
    stayOrDropOff,
    selectedDate,
    selectedTime,
    complimentaryRide,
    hasAnyTransportOption,
    appointment?.shopData?.requireComments,
    appointment?.comments,
  ]);

  // Make the ride indicator show as completed if already selected in Redux
  useEffect(() => {
    if (hasAnyTransportOption && stayOrDropOff === "drop") {
      setBounceComplimentaryRide(Boolean(complimentaryRide));
    }
  }, [hasAnyTransportOption, complimentaryRide, stayOrDropOff]);

  useEffect(() => {
    if (stayOrDropOff && stayOrDropOff !== previousStayOrDropOffRef.current) {
      scrollIntoViewOnMobile("date-picker");
    }

    previousStayOrDropOffRef.current = stayOrDropOff;
  }, [stayOrDropOff]);

  useEffect(() => {
    if (selectedDate && selectedDate !== previousSelectedDateRef.current) {
      scrollIntoViewOnMobile("time-picker");
    }

    previousSelectedDateRef.current = selectedDate;
  }, [selectedDate]);

  useEffect(() => {
    if (selectedTime && selectedTime !== previousSelectedTimeRef.current) {
      if (hasAnyTransportOption && stayOrDropOff === "drop") {
        scrollIntoViewSmooth("ride-picker");
      } else {
        scrollIntoViewOnMobile("booking-next-button");
      }
    }

    previousSelectedTimeRef.current = selectedTime;
  }, [hasAnyTransportOption, selectedTime, stayOrDropOff]);

  useEffect(() => {
    if (
      complimentaryRide &&
      complimentaryRide !== previousComplimentaryRideRef.current
    ) {
      scrollIntoViewOnMobile("booking-next-button");
    }

    previousComplimentaryRideRef.current = complimentaryRide;
  }, [complimentaryRide]);

  useEffect(() => {
    if (
      previousAppointmentOptionsStepRef.current !== "comments" &&
      appointment?.appointmentOptionsStep === "comments"
    ) {
      scrollIntoViewOnMobile("appointment-summary");
    }

    previousAppointmentOptionsStepRef.current =
      appointment?.appointmentOptionsStep;
  }, [appointment?.appointmentOptionsStep]);

  // Auto-manage the time slot and ride choice based on the after-hours drop-off opt-in.
  // When ON: set time to one hour before that day's opening time and default
  //   the ride choice to "None" (shops don't offer transport for after-hours drops).
  // When toggled OFF after being ON: clear the auto-set time and ride choice.
  useEffect(() => {
    const wasRequested = previousAfterHoursRequestedRef.current;
    previousAfterHoursRequestedRef.current = afterHoursRequested;

    if (afterHoursRequested) {
      if (complimentaryRide !== "None") {
        setComplimentaryRide("None");
        dispatch(updateAppointment({ updates: { complimentaryRide: "None" } }));
      }

      if (!selectedDate) return;
      const schedule = appointment?.shopData?.shopSchedule ?? [];
      const dayOfWeek = dayjs(selectedDate).day();
      const entry = schedule.find((s) => s.day === dayOfWeek && s.open);
      if (!entry) return;

      const { openTime } = getScheduleTimes(entry);
      if (!openTime) return;

      const [h, m] = openTime.split(":").map(Number);
      if (Number.isNaN(h) || Number.isNaN(m)) return;

      // Format must match TimePicker slot format ("h:mm AM/PM") so downstream
      // calendar parsing in utils/calendar.ts works.
      const autoTime = dayjs(selectedDate)
        .hour(h)
        .minute(m)
        .subtract(1, "hour")
        .format("h:mm A");

      if (autoTime !== selectedTime) {
        setSelectedTime(autoTime);
        dispatch(updateAppointment({ updates: { time: autoTime } }));
      }
      return;
    }

    // Only clear when the flag transitioned from true → false, so we don't
    // clobber a manually-selected time/ride on initial mount.
    if (wasRequested) {
      if (selectedTime) {
        setSelectedTime("");
        dispatch(updateAppointment({ updates: { time: "" } }));
      }
      if (complimentaryRide) {
        setComplimentaryRide("");
        dispatch(updateAppointment({ updates: { complimentaryRide: "" } }));
      }
    }
  }, [
    afterHoursRequested,
    selectedDate,
    appointment?.shopData?.shopSchedule,
    selectedTime,
    complimentaryRide,
    dispatch,
  ]);

  const fetchSlotTimesForMonth = useCallback(async (month: dayjs.Dayjs): Promise<void> => {
    const currentAppointment = appointmentRef.current;
    if (!currentAppointment) return;

    setLoading(true);

    // Start from the later of the 1st of the month or the earliest bookable date
    const monthStart = month.startOf("month");
    const shopToday = getShopNow(currentAppointment.shopData).startOf("day");
    const earliestBookable = sameDayBookings
      ? shopToday
      : shopToday.add(1, "day");
    const rangeStart = monthStart.isAfter(earliestBookable)
      ? monthStart
      : earliestBookable;

    // End at the last day of the month + 1 (exclusive range)
    const rangeEnd = month.endOf("month").add(1, "day").startOf("day");

    const prospectiveAppointment: ProspectiveCalendarAppointment = {
      ...{
        ...currentAppointment,
        shopId: currentAppointment.shopData?.shopId,
        shopData: undefined,
      },
      apptType: stayOrDropOff,
      startDateRange: rangeStart.toDate(),
      endDateRange: rangeEnd.toDate(),
    };
    const result: AvailableSlotsInterfaceV2 = await getBookingTimesV2(
      prospectiveAppointment
    );

    setAvailabilityResponse(result);

    // Update no-slots message for service-specific scenarios
    const hasAnySlots = Object.values(result.dates || {}).some(
      (daySlots) =>
        (daySlots.waiterSlots && daySlots.waiterSlots.length > 0) ||
        (daySlots.dropOffSlots && daySlots.dropOffSlots.length > 0)
    );

    if (
      !hasAnySlots &&
      (currentAppointment?.service || currentAppointment?.services?.length)
    ) {
      setNoSlotsMessage(generateServiceSpecificMessage(currentAppointment));
    }

    setLoading(false);
  }, [generateServiceSpecificMessage, sameDayBookings, stayOrDropOff]);

  // Fetch slot times when appointment type or viewed month changes
  useEffect(() => {
    if (stayOrDropOff) {
      fetchSlotTimesForMonth(currentMonth);
    }
    // Don't clear restored selections on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      setSelectedDate(null);
      setSelectedTime("");
      if (appointmentRef.current?.afterHoursDropOffRequested) {
        dispatch(
          updateAppointment({ updates: { afterHoursDropOffRequested: false } })
        );
      }
    }
  }, [stayOrDropOff, currentMonth, fetchSlotTimesForMonth, dispatch]);

  // Clear selected time when date changes (skip on initial mount)
  const skipNextDateClear = useRef(true);
  useEffect(() => {
    if (skipNextDateClear.current) {
      skipNextDateClear.current = false;
    } else {
      setSelectedTime("");
    }
  }, [selectedDate]);

  // Accent color & gradient (matching stage 1 pattern)
  const accentColor = appointment?.shopData?.color || "#2b73b9";
  const { gradientBackground } = useMemo(
    () => getGradientFromColor(accentColor),
    [accentColor]
  );

  return (
    <div
      className="px-4 py-5 md:px-6 md:pt-2 md:pb-0 min-h-full"
      style={{ background: gradientBackground }}
    >
      {isCommentsStep && isAppointmentComplete ? (
        /* Full-width summary-only view */
        <div className="max-w-xl mx-auto md:py-10">
          <AppointmentSummary
            appointment={appointment}
            selectedDate={selectedDate}
            selectedTime={selectedTime}
            stayOrDropOff={stayOrDropOff!}
            setClear={setClear}
            onEdit={() => {
              dispatch(
                updateAppointment({
                  updates: { appointmentOptionsStep: "selection" },
                })
              );
            }}
          />
        </div>
      ) : (
        /* Two-column desktop / single-column mobile editing grid */
        <div className="md:max-h-[68vh] md:overflow-y-auto md:pr-1 md:pb-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Left column */}
            <div className="flex flex-col gap-4">
              {/* Stay or Drop Off card */}
              <StayOrDropSelector
                appointment={appointment}
                stayOrDropOff={stayOrDropOff}
                setStayOrDropOff={setStayOrDropOff}
              />

              {/* Date picker card */}
              {stayOrDropOff && (
                <DatePicker
                  appointment={appointment}
                  selectedDate={selectedDate}
                  setSelectedDate={setSelectedDate}
                  currentMonth={currentMonth}
                  onMonthChange={setCurrentMonth}
                  availabilityResponse={availabilityResponse}
                  loadingDates={loading}
                />
              )}
            </div>

            {/* Right column */}
            <div className="flex flex-col gap-4">
              {/* After-hours drop-off opt-in (shown above time slot) */}
              {selectedDate &&
                stayOrDropOff === "drop" &&
                appointment?.shopData?.afterHoursDropOff?.enabled && (
                  <AfterHoursDropOffCard appointment={appointment} />
                )}

              {/* Time picker card — hidden when after-hours opt-in is on */}
              {selectedDate && !afterHoursRequested && (
                <TimePicker
                  appointment={appointment}
                  selectedTime={selectedTime}
                  setSelectedTime={setSelectedTime}
                  setComplimentaryRide={setComplimentaryRide}
                  bookingTimes={
                    (stayOrDropOff === "wait"
                      ? availabilityResponse?.dates[selectedDate]?.waiterSlots
                      : availabilityResponse?.dates[selectedDate]?.dropOffSlots) ??
                    []
                  }
                  loading={loading}
                  noSlotsMessage={noSlotsMessage}
                  setClear={setClear}
                  setBounceComplimentaryRide={setBounceComplimentaryRide}
                />
              )}

              {/* Ride option picker card — hidden when after-hours opt-in
                  is on; ride choice is auto-defaulted to "None" in that case. */}
              {selectedTime &&
                hasAnyTransportOption &&
                stayOrDropOff === "drop" &&
                !afterHoursRequested && (
                <RideOptionPicker
                  appointment={appointment}
                  complimentaryRide={complimentaryRide}
                  setComplimentaryRide={setComplimentaryRide}
                  bounceComplimentaryRide={bounceComplimentaryRide}
                  setBounceComplimentaryRide={setBounceComplimentaryRide}
                  setClear={setClear}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export { AppointmentPickerV2 };
